import React, { useState } from 'react'
import PropTypes from 'prop-types'

import PreviewMediaCard from 'components/Shared/MediaCard/PreviewMediaCard'
import MediaListContainer from '../../MediaListV2/MediaListContainer'
import { useTranslation } from 'react-i18next'
import { SecondaryButton, TransparentBgButton } from 'components/Shared/Button'
import { useToast } from 'components/Shared/AppToast/ToastProvider'
import useGeneratedModal from './useGeneratedModal'
import { HeadingIcon } from 'images'
import { MobileMediaPreview } from '../MediaPreview'

export default function GeneratedImagesModal({ variants, closeModal, onSubmitSelections }) {
  const { t } = useTranslation()
  const { addNotification } = useToast()

  const {
    selectedVariants,
    setSelectedVariants,
    isGroupFullySelected,
    toggleGroupSelection,
  } = useGeneratedModal(variants)

  const [mobilePreviewFile, setMobilePreviewFile] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const showSubmitError = () => {
    addNotification({
      message: t('common.genericError'),
      description: t('common.errors.inServerResponse'),
      variant: 'error',
    })
  }

  const submitSelections = async (selectedIds) => {
    if (!onSubmitSelections) {
      closeModal()
      return
    }

    setIsSubmitting(true)

    try {
      const response = await onSubmitSelections(selectedIds)

      if (!response?.success) {
        showSubmitError()
        return
      }

      closeModal()
    } catch {
      showSubmitError()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDiscard = () => {
    submitSelections([])
  }

  const handleApply = () => {
    if (!selectedVariants.length) {
      return
    }

    submitSelections(selectedVariants)
  }

  return (
    <div className='generated-images-modal'>
      <div className='generated-images-modal__body'>
        {variants.map((variant) => (
          <div key={variant.id} className='section'>
            <div className='section-content'>
              {variant.groups.map((group) => (
                <div key={group.id} className='group'>
                  <div className='group-header'>
                    {group.headline ? 
                      <p className='headline'>
                        <HeadingIcon width={14} height={14} />
                        <span>{group.headline}</span>
                      </p> 
                      : 
                      (<span style={{ color: '#4F4F4F' }}>
                        {t('Recommendations.campaign.setup.coCreatePanel.generatedImagesModal.aspectRatioVariants')}
                      </span>
                      )}

                    {group.images.length > 1 && (
                      <button
                        type='button'
                        className='select-button'
                        disabled={isSubmitting}
                        onClick={() => toggleGroupSelection(group)}
                      >
                        {t(
                          isGroupFullySelected(group)
                            ? 'Recommendations.campaign.setup.coCreatePanel.generatedImagesModal.deselectAll'
                            : 'Recommendations.campaign.setup.coCreatePanel.generatedImagesModal.selectAll',
                        )}
                      </button>
                    )}
                  </div>

                  <MediaListContainer className='m-0' fadeColor='background'>
                    {group.images.map((image) => (
                      <PreviewMediaCard
                        key={image.id}
                        item={image}
                        selectedMedia={selectedVariants}
                        selectiveMode={true}
                        setSelectedMedia={setSelectedVariants}
                        onExpandClick={setMobilePreviewFile}
                      />
                    ))}
                  </MediaListContainer>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className='modal-footer'>
        <div className='modal-footer__info'>
          <p>{t('Recommendations.campaign.setup.coCreatePanel.generatedImagesModal.selectedVariantsCount', { countSelected: selectedVariants.length })}</p>
        </div>

        <div className='d-flex gap-2'>
          <TransparentBgButton
            text={t('common.cancel')}
            color="navy"
            fullWidth={false}
            disabled={isSubmitting}
            onClick={handleDiscard}
          />

          <SecondaryButton 
            text={t('Recommendations.campaign.setup.coCreatePanel.generatedImagesModal.apply')}
            color="navy"
            disabled={!selectedVariants.length || isSubmitting}
            loading={isSubmitting}
            onClick={handleApply} />
        </div>
      </div>

      <MobileMediaPreview file={mobilePreviewFile} onClose={() => setMobilePreviewFile(null)} />
    </div>
  )
}

GeneratedImagesModal.propTypes = {
  variants: PropTypes.arrayOf(PropTypes.object),
  closeModal: PropTypes.func.isRequired,
  onSubmitSelections: PropTypes.func,
}
